import { Button } from "@/components/ui/button";
import { cn } from "@/components/utils/utils";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Plus, ChevronDown } from "lucide-react";
import { ComponentProps } from "react";
import { useI18n } from "@/hooks/useI18n";
import { ACTIVITIES, ActivityType } from "../models/activity-configs.model";

interface ActivityTypePickerProps {
  onSelect: (type: ActivityType) => void;
  /** Testo del trigger; default la label usata nel builder ("Aggiungi attività"). */
  label?: string;
  /** Variante del bottone trigger; default "outline" (aspetto usato nel builder). */
  variant?: ComponentProps<typeof Button>["variant"];
  /** Size del bottone trigger; default quello di default del Button. */
  size?: ComponentProps<typeof Button>["size"];
  /** Classi extra sul trigger (es. per un CTA compatto nell'header). */
  className?: string;
}

/**
 * Dropdown "Aggiungi attività": elenca i tipi visibili di `ACTIVITIES` e notifica
 * `onSelect` con il tipo scelto. Estratto da DayBuilderStep per essere riusato
 * anche fuori dal builder (es. LibraryPage).
 */
export function ActivityTypePicker({ onSelect, label, variant = "outline", size, className }: ActivityTypePickerProps) {
  const { t } = useI18n();

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant={variant} size={size} className={cn("flex items-center gap-2", className)}>
          <Plus className="h-4 w-4" />
          <span className="hidden sm:inline">{label ?? t('builder.addActivity')}</span>
          <ChevronDown className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        {ACTIVITIES.filter(type => !type.hidden).map((type) => (
          <DropdownMenuItem
            key={type.id}
            onClick={() => onSelect(type.id)}
            className="flex items-center gap-2 my-1"
          >
            {type.icon}
            {t(type.labelKey as any)}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
